import type * as FirebaseClientModule from '@akpl/firebase/client';
import type { FirebaseClient, FirebaseClientConfig } from '@akpl/firebase/client';

import type { BridgeKind } from './bridge';

let client: FirebaseClient | null = null;
let sdk: Promise<typeof FirebaseClientModule> | null = null;

/**
 * Where the TV session is kept between page loads: **wherever the device id is kept**, which
 * since QA3 T2 is both hosts. The APK persists the id in Android, the browser bridge persists
 * it in localStorage (`DEVICE_ID_KEY`), so in both a reload or a power-cycle must come back
 * into the running session (docs/04 §1.3) instead of asking the room to pair again.
 *
 * This reverses the in-memory browser persistence of QA TV-46 (2026-09-14, morning), which
 * only made sense while a web TV minted a new device id on every load. Testing pairing
 * repeatedly is what `?fresh=1` is for (`wantsFreshDevice`): it mints a new device id *and*
 * signs the persisted user out, which is the case TV-46 was really about.
 */
export function persistenceFor(_bridge: BridgeKind): 'idb-first' {
  return 'idb-first';
}

/**
 * Starts downloading the Firebase chunk without waiting for anything (UX-110).
 *
 * Only `initFirebase` needs `/config`; the *download* does not. Boot used to await `/config`
 * and only then reach the dynamic import, so two of the slowest steps of a cold start ran one
 * after the other — on a stick, for several seconds. Called at the top of `runBoot`, the chunk
 * arrives while `GET /config` is still in flight.
 */
export function prefetchFirebase(): void {
  sdk ??= import('@akpl/firebase/client');
}

/**
 * Lazily loads the Firebase SDK (separate chunk) and initializes the client. Returns null
 * when `/config` has no Firebase project, which puts the shell in "offline auth" mode.
 */
export async function loadFirebase(
  config: FirebaseClientConfig,
  bridge: BridgeKind = 'mock',
): Promise<FirebaseClient | null> {
  if (client) return client;
  sdk ??= import('@akpl/firebase/client');
  const mod = await sdk;
  if (!mod.hasFirebaseConfig(config)) return null;
  client = mod.initFirebase(config, { persistence: persistenceFor(bridge) });
  return client;
}

/** The client once loaded (RTDB listener hooks in WP1.6 read it from here). */
export function getFirebase(): FirebaseClient | null {
  return client;
}

/** Test helper. */
export function setFirebaseForTests(next: FirebaseClient | null): void {
  client = next;
}
